import type { GfmFloodFeed, GfmObservation } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  feed: GfmFloodFeed | null;
  ageMinutes?: number | null;
  fallbackTier?: "live" | "unavailable" | "scenario" | "cache" | "database" | "reference";
  loading?: boolean;
}

/**
 * Observed flood extent — where the water actually was on the ground.
 *
 * THE RULE THIS PANEL EXISTS TO ENFORCE
 * --------------------------------------
 * There is exactly one honest way to fill this panel, and it is the one below:
 * print the date Sentinel-1 flew, the area counted off the raster, the cells it
 * was counted from, and the method. Nothing here is scored, ranked or coloured
 * against a scale we invented. There is no "flood severity" band on this panel,
 * because GFM does not publish one and deriving one would be us inventing a
 * number and attributing it to a source that never said it.
 *
 * The other rule is the empty state. "No pass in 21 days saw water here" is NOT
 * "there is no flood". A satellite that has not flown over the district cannot
 * report an absence, and rendering a blank raster next to a live gauge layer
 * would invite exactly that reading. So an empty scan says what it actually
 * knows — that it looked, and found nothing — and stops there.
 */
function ageLabel(o: GfmObservation): string {
  if (o.ageDays === 0) return "today";
  if (o.ageDays === 1) return "yesterday";
  return `${o.ageDays} days ago`;
}

export default function GfmExtentPanel({ feed, ageMinutes, fallbackTier, loading }: Props) {
  if (loading && !feed) {
    return (
      <section className="panel">
        <PanelHeader title="OBSERVED FLOOD EXTENT" source="copernicus-gfm" />
        <p className="gfm-empty">Reading the Sentinel-1 record…</p>
      </section>
    );
  }

  if (!feed) {
    return (
      <section className="panel">
        <PanelHeader title="OBSERVED FLOOD EXTENT" source="copernicus-gfm" fallbackTier="unavailable" />
        <p className="gfm-empty">No scan available.</p>
      </section>
    );
  }

  const { latest, observations } = feed;

  // Nothing in the window. Say exactly what was searched and what was not found,
  // and do not let the reader mistake a blank map for a dry district.
  if (!latest) {
    return (
      <section className="panel">
        <PanelHeader title="OBSERVED FLOOD EXTENT" source="copernicus-gfm" ageMinutes={ageMinutes} fallbackTier={fallbackTier ?? "unavailable"} />
        <p className="gfm-empty">
          No Sentinel-1 pass in the last {feed.scanDays} days recorded inundation in this district.
        </p>
        <p className="gfm-empty__caveat">
          This is not an all-clear. It means the radar saw no water on the passes it made —
          it does not measure river level, gauge condition, or anything that happened between passes.
          Newest date checked: {feed.latestScanDate}.
        </p>
      </section>
    );
  }

  const earliest = observations[observations.length - 1];
  // Recession is a measured trend, not a forecast: it is the difference between
  // two raster counts on two dates. It is only shown when both ends exist.
  const changeKm2 = earliest && earliest !== latest ? (latest.floodedKm2 ?? 0) - (earliest.floodedKm2 ?? 0) : null;

  return (
    <section className="panel">
      <PanelHeader
        title="OBSERVED FLOOD EXTENT"
        source="copernicus-gfm"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier ?? "live"}
      />

      <div className="gfm-headline">
        <div className="gfm-area">
          <span className="gfm-area__value">{latest.floodedKm2 ?? "—"}</span>
          <span className="gfm-area__unit">km²</span>
        </div>
        <div className="gfm-headline__meta">
          <div className="gfm-date">
            Sentinel-1 pass <strong>{latest.observationDate}</strong> · {ageLabel(latest)}
          </div>
          <div className="gfm-cells">
            {latest.floodCells.toLocaleString("en-GB")} cells at {feed.pixelSizeM} m
            {" · "}{feed.resolutionM} m product
          </div>
        </div>
      </div>

      {changeKm2 !== null && (
        <p className="gfm-trend">
          {changeKm2 < 0
            ? `Down ${Math.abs(changeKm2).toFixed(2)} km² since ${earliest!.observationDate}`
            : `Up ${changeKm2.toFixed(2)} km² since ${earliest!.observationDate}`}
          <span className="gfm-trend__note"> · measured change between two passes</span>
        </p>
      )}

      {observations.length > 1 && (
        <ul className="gfm-list">
          {observations.map((o) => (
            <li key={o.observationDate} className="gfm-list__row">
              <span className="gfm-list__date mono">{o.observationDate}</span>
              <span className="gfm-list__age">{ageLabel(o)}</span>
              <span className="gfm-list__area mono">{o.floodedKm2 ?? "—"} km²</span>
              <span className="gfm-list__cells mono">{o.floodCells.toLocaleString("en-GB")} cells</span>
            </li>
          ))}
        </ul>
      )}

      <p className="gfm-method" lang="en">{feed.methodEn}</p>
      <p className="gfm-method gfm-method--th" lang="th">{feed.methodTh}</p>
      <p className="gfm-foot">
        Drawn from the GFM WMS-T at {feed.wmsBase.split("/").slice(-1)[0]}. An observation is a
        past pass, not a live feed — zoom the map to read the extent on the ground.
      </p>
    </section>
  );
}